import type { Store } from '@blocksuite/affine/store';

/**
 * URL scheme for the source-side marker that points at a saved
 * notebook entry. Distinct from the card and branch schemes so the
 * global CanvasLinkRouter can route each click to its own handler.
 */
export const NOTES_LINK_SCHEME = 'curiescious-notes://';

export function notesHref(notesDocId: string, entryId: string): string {
  return `${NOTES_LINK_SCHEME}${encodeURIComponent(notesDocId)}/${encodeURIComponent(entryId)}`;
}

export function parseNotesHref(
  href: string
): { notesDocId: string; entryId: string } | null {
  if (!href.startsWith(NOTES_LINK_SCHEME)) return null;
  const rest = href.slice(NOTES_LINK_SCHEME.length);
  const sep = rest.indexOf('/');
  if (sep <= 0) return null;
  try {
    return {
      notesDocId: decodeURIComponent(rest.slice(0, sep)),
      entryId: decodeURIComponent(rest.slice(sep + 1)),
    };
  } catch {
    return null;
  }
}

/**
 * Amber accent for notes markers — matches the `Notes` workspace tag
 * (`#D97706`) so the researcher reads the color as "this is in my
 * notes." Opacity tuned to read clearly on light backgrounds without
 * fighting body typography.
 */
const NOTES_BACKGROUND = 'rgba(217, 119, 6, 0.22)';

type FormatTarget = {
  text?: {
    format: (
      index: number,
      length: number,
      attrs: Record<string, unknown>
    ) => void;
    toString: () => string;
  } | null;
};

/**
 * Paint the source-side marker for a notes entry: amber background
 * + a `link` carrying the notes-doc id and the entry's anchor block.
 * Click routes through CanvasLinkRouter → navigates to the Notes
 * doc and scrolls to the entry's divider block.
 */
export function applyNotesLink(
  doc: Store,
  sourceBlockId: string,
  range: { index: number; length: number },
  notesDocId: string,
  entryId: string
): void {
  const target = doc.getBlock(sourceBlockId)?.model as FormatTarget | undefined;
  if (!target?.text) return;
  if (range.length <= 0) return;
  try {
    target.text.format(range.index, range.length, {
      background: NOTES_BACKGROUND,
      link: notesHref(notesDocId, entryId),
    });
  } catch (err) {
    console.warn('[notes-link] format failed', err);
  }
}

/**
 * Clear the notes marker on undo. Sets background + link to null so
 * the inline format is removed (BlockSuite treats null as "clear
 * this attribute on the range").
 */
export function removeNotesLink(
  doc: Store,
  sourceBlockId: string,
  range: { index: number; length: number }
): void {
  const target = doc.getBlock(sourceBlockId)?.model as FormatTarget | undefined;
  if (!target?.text) return;
  if (range.length <= 0) return;
  try {
    target.text.format(range.index, range.length, {
      background: null,
      link: null,
    });
  } catch {
    /* range gone */
  }
}
